import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';
import {
  FIELD_BADGE_CELL_GAP,
  computeFieldBadgeOffset,
  type FieldBadgeConfiguration,
  type FieldBadgeInset,
  type FieldBadgeOffset,
} from '../lib/field-badge-anchor';

export interface TableFieldBadgeTarget {
  key: string;
  rowIndex: number;
  colIndex: number;
  configuration: FieldBadgeConfiguration;
  inset: FieldBadgeInset;
}

export interface TableFieldBadge {
  key: string;
  configuration: FieldBadgeConfiguration;
  style: Record<string, string>;
}

export function useTableFieldBadges(
  gridRef: Ref<HTMLElement | null>,
  targets: Ref<TableFieldBadgeTarget[]>,
  layoutVersion: Ref<number>
) {
  // Only geometry is measured and cached. Everything presentational (the configuration and the
  // decoration inset) is read live from `targets` by the computed below, so switching
  // anchor <-> report or attaching a rule over the same cells re-renders without needing a
  // re-measure — a measure pass keyed on cell identity would miss it.
  const offsets = ref<Record<string, FieldBadgeOffset>>({});
  let observer: ResizeObserver | null = null;

  const coordsKey = (rowIndex: number | string, colIndex: number | string) =>
    `${rowIndex}:${colIndex}`;

  // One indexed pass instead of a `querySelector` per marked field. First match wins so a nested
  // cell never shadows the outer one, matching `querySelector` document order.
  const collectCells = (grid: HTMLElement): Map<string, HTMLElement> => {
    const cells = new Map<string, HTMLElement>();

    for (const cell of Array.from(grid.querySelectorAll<HTMLElement>('.table-cell'))) {
      const { rowIndex, colIndex } = cell.dataset;
      if (rowIndex === undefined || colIndex === undefined) continue;

      const key = coordsKey(rowIndex, colIndex);
      if (!cells.has(key)) cells.set(key, cell);
    }

    return cells;
  };

  const findCell = (cells: Map<string, HTMLElement>, target: TableFieldBadgeTarget) =>
    cells.get(coordsKey(target.rowIndex, target.colIndex)) ?? null;

  const measure = () => {
    const grid = gridRef.value;

    if (!grid || targets.value.length === 0) {
      offsets.value = {};
      return;
    }

    const cells = collectCells(grid);
    const measured: Record<string, FieldBadgeOffset> = {};

    for (const target of targets.value) {
      const offset = computeFieldBadgeOffset(findCell(cells, target), grid);
      if (offset) measured[target.key] = offset;
    }

    offsets.value = measured;
  };

  const badges = computed<TableFieldBadge[]>(() =>
    targets.value.flatMap((target) => {
      const offset = offsets.value[target.key];
      if (!offset) return [];

      return [
        {
          key: target.key,
          configuration: target.configuration,
          style: {
            top: `${offset.top + FIELD_BADGE_CELL_GAP + target.inset.top}px`,
            left: `${offset.left - FIELD_BADGE_CELL_GAP - target.inset.right}px`,
          },
        },
      ];
    })
  );

  const observeTargets = () => {
    const grid = gridRef.value;
    if (!observer || !grid) return;

    observer.disconnect();
    observer.observe(grid);

    if (targets.value.length === 0) return;

    const cells = collectCells(grid);

    for (const target of targets.value) {
      const cell = findCell(cells, target);
      if (cell) observer.observe(cell);
    }
  };

  const refresh = () => {
    void nextTick(() => {
      observeTargets();
      measure();
    });
  };

  onMounted(() => {
    observer = new ResizeObserver(measure);
    refresh();
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  watch([() => targets.value.map((target) => target.key).join('|'), layoutVersion], refresh);

  return { badges };
}
